import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'

import { CREDIT_CARD_PAYMENT_METHOD, VOUCHER } from './constants'

import { fetchCustomerCreditCardRequestAction } from 'src/effects/User/actions'
import { getCreditCard } from 'src/effects/User/selectors'
import { useMarket } from 'src/hooks/useMarket'
import { useSystem } from 'src/hooks/useSystem'
import { typePaymentMethods } from 'client/types/checkout'
import PaymentMethodsLogos from 'src/comps/PaymentMethodsLogos'

import PaymentConfirmationLoader from './PaymentConfirmationLoader'
import PaymentErrorMessage from './PaymentErrorMessage'
import PaymentModal from 'src/comps/Molecules/PaymentModal'
import PaymentData from './PaymentData'
import { useSession } from 'src/hooks/useSession'

import styles from './styles.module.css'

const paymentConfirmationTitle = ({ consumer, isPrime }) => {
  if (consumer === VOUCHER) {
    return { id: 'payment.changing-creditcard' }
  }
  if (isPrime) {
    return { id: 'global.completing-your-payment' }
  }
  return { id: 'global.confirming-booking' }
}

const paymentConfirmationMessage = ({ isPrime }) => {
  if (!isPrime) {
    return { id: 'checkout.confirming-booking-message' }
  }
  return { id: 'global.please-wait-few-seconds' }
}

const PaymentForm = ({
  cancellationDate,
  consumer,
  error,
  subCode,
  isConfirming,
  paymentModalData,
  isPrime,
  onError,
  onPaymentMethodChange,
  onSuccess,
  paymentMethods,
  payNow,
  purchaseId,
  sellingRate,
  disabled,
}) => {
  const dispatch = useDispatch()

  const creditCard = useSelector(getCreditCard)

  const { market } = useMarket()
  const { apiHostName } = useSystem()

  const { isLogged } = useSession()

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(CREDIT_CARD_PAYMENT_METHOD)

  useEffect(() => {
    if (isLogged) {
      dispatch(fetchCustomerCreditCardRequestAction({ hostname: apiHostName, market }))
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const onSelectedPaymentMethodChange = useCallback(
    paymentMethod => {
      setSelectedPaymentMethod(paymentMethod)

      if (onPaymentMethodChange) {
        onPaymentMethodChange(paymentMethod)
      }
    },
    [onPaymentMethodChange]
  )

  return (
    <section className={styles.paymentForm} data-testid="payment-form">
      <h3 className={`${styles.title} ${styles.desktop}`}>
        <FormattedMessage id="global.confirm-booking" />
      </h3>
      <div className={`${styles.title} ${styles.mobile}`}>
        <FormattedMessage id="global.credit-debit-card" />
      </div>

      <div className={styles.subtitle}>
        <FormattedMessage id="global.payment-secure" />
      </div>

      <div className={styles.paymentLogos}>
        <PaymentMethodsLogos checkout />
      </div>

      {error && !isConfirming && <PaymentErrorMessage subCode={subCode} />}

      {isConfirming && (
        <PaymentConfirmationLoader
          title={paymentConfirmationTitle({ consumer, isPrime })}
          message={paymentConfirmationMessage({ isPrime })}
        />
      )}

      <PaymentData
        creditCard={creditCard}
        disabled={disabled || isConfirming}
        onFail={onError}
        onSuccess={onSuccess}
        paymentMethods={paymentMethods}
        purchaseId={purchaseId}
        show={!!isConfirming}
        onSelectedPaymentMethodChange={onSelectedPaymentMethodChange}
        selectedPaymentMethod={selectedPaymentMethod}
        sellingRate={sellingRate}
        cancellationDate={cancellationDate}
        payNow={payNow}
      />

      {paymentModalData && <PaymentModal iframeData={paymentModalData} onFail={onError} />}
    </section>
  )
}

PaymentForm.propTypes = {
  cancellationDate: PropTypes.string,
  consumer: PropTypes.string,
  error: PropTypes.bool,
  subCode: PropTypes.number,
  isConfirming: PropTypes.bool,
  paymentModalData: PropTypes.shape({
    url: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }),
  isPrime: PropTypes.bool,
  onError: PropTypes.func.isRequired,
  onPaymentMethodChange: PropTypes.func,
  onSuccess: PropTypes.func.isRequired,
  paymentMethods: typePaymentMethods,
  payNow: PropTypes.number,
  purchaseId: PropTypes.string,
  sellingRate: PropTypes.number,
  disabled: PropTypes.bool,
}

export default PaymentForm
